import React from "react";
import { Box, Typography, Sheet, Stack } from "@mui/joy";
import { Send, RotateCw, FileDown } from "lucide-react";
import { Button } from "../../shared";
import SideModal from "../SideModal";
import logoUrl from "../../assets/images/logo.png";

interface IContractPreview {
    open: boolean;
    onClose: () => void;
    data: any;
    onGenerate?: (overrides: any) => void;
    isGenerating?: boolean;
    onSend?: () => void;
    isSending?: boolean;
    isSendDisabled?: boolean;
    onDownload?: () => void;
    isDownloading?: boolean;
    isDownloadDisabled?: boolean;
}

const ADDITIONAL_INFO_ITEMS = [
    "Roofing debris removal fee included",
    "Property to be cleaned of all project-related debris with magnet",
    "Clean gutters of all project-related roof debris",
    "Building Permit: Required - Billed at Final Invoice",
];

export default function ContractPreview({
    open,
    onClose,
    data,
    onGenerate,
    isGenerating,
    onSend,
    isSending,
    isSendDisabled,
    onDownload,
    isDownloading,
    isDownloadDisabled,
}: IContractPreview) {
    const [localData, setLocalData] = React.useState(data);

    React.useEffect(() => {
        setLocalData(data);
    }, [data]);

    const COLOR_HEADER = "#1D3557";
    const COLOR_INSURANCE = "#FFFDEC";

    const display = (value?: string) =>
        value && value !== "N/A" && value.trim() !== "" ? value : "—";

    const cellTextSx = {
        fontSize: "11px",
        fontWeight: 600,
        color: "neutral.800",
    } as const;

    const GridCell = ({
        label,
        value,
        flex = 1,
        multiline = false,
        children,
    }: {
        label: string;
        value?: string;
        flex?: number | string;
        multiline?: boolean;
        children?: React.ReactNode;
    }) => (
        <Box
            sx={{
                flex,
                px: 1,
                py: 0.5,
                border: "0.2px solid",
                borderColor: "neutral.200",
                bgcolor: "white",
                minWidth: 0,
                alignSelf: "stretch",
            }}
        >
            <Typography
                sx={{ fontSize: "8px", fontWeight: 700, color: "neutral.500", textTransform: "uppercase", mb: 0.2 }}
            >
                {label}
            </Typography>
            {children ?? (
                <Typography
                    sx={{
                        ...cellTextSx,
                        whiteSpace: multiline ? "pre-line" : "normal",
                        overflow: "visible",
                        wordBreak: "break-word",
                    }}
                >
                    {display(value)}
                </Typography>
            )}
        </Box>
    );

    const PitchRowsContent = ({
        rows,
        fallback,
    }: {
        rows?: Array<{ section: string; pitch: string; squares: string }>;
        fallback?: string;
    }) => {
        if (!rows?.length) {
            return <Typography sx={cellTextSx}>{display(fallback)}</Typography>;
        }

        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.35, mt: 0.25 }}>
                {rows.map((row, index) => (
                    <Box
                        key={`${row.section}-${row.pitch}-${index}`}
                        sx={{
                            display: "grid",
                            gridTemplateColumns:
                                "minmax(0, 1.5fr) minmax(52px, 0.8fr) minmax(36px, 0.5fr)",
                            columnGap: 1.25,
                            alignItems: "baseline",
                        }}
                    >
                        <Typography sx={cellTextSx} noWrap>
                            {row.section}
                        </Typography>
                        <Typography sx={cellTextSx}>{row.pitch}</Typography>
                        <Typography sx={cellTextSx}>{row.squares}</Typography>
                    </Box>
                ))}
            </Box>
        );
    };

    const SectionHeader = ({ num, title }: { num: string; title: string }) => (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                bgcolor: COLOR_HEADER,
                px: 1.5,
                py: 0.5,
                mt: 2,
            }}
        >
            <Typography sx={{ fontWeight: 800, fontSize: "12px", mr: 1.5, opacity: 0.8, color: "white" }}>
                {num}
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: "11px", letterSpacing: "0.05em", color: "white" }}>
                {title}
            </Typography>
        </Box>
    );

    const scope = localData?.RoofingScope;
    const vents = localData?.NailingVentilation;
    const gutters = localData?.GutterScope;
    const pricing = localData?.Pricing;

    return (
        <SideModal
            showAdditionalHeadersIcons={false}
            open={open}
            onClose={onClose}
            portalBoxProps={{ width: 850 }}
            title="Document Preview"
            description="Service agreement preview."
        >
            <Box sx={{ bgcolor: "background.level1", minHeight: "100%", display: "flex", flexDirection: "column" }}>
                <Box sx={{ p: 0, my: 3, flex: 1, display: "flex", justifyContent: "center", mb: 3, width: "100%" }}>
                    <Sheet
                        sx={{
                            width: "100%",
                            maxWidth: "800px",
                            bgcolor: "white",
                            p: 5,
                            display: "flex",
                            flexDirection: "column",
                            gap: 0,
                            fontFamily: "'Outfit', sans-serif",
                            border: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                            <img
                                src={logoUrl}
                                alt="1st Colorado Roofing"
                                style={{ height: 40, width: "auto", objectFit: "contain" }}
                            />
                            <Box sx={{ textAlign: "right" }}>
                                <Typography sx={{ fontWeight: 900, fontSize: "18px", color: COLOR_HEADER }}>
                                    SERVICE AGREEMENT
                                </Typography>
                                <Typography sx={{ fontSize: "10px", color: "neutral.500", fontWeight: 600 }}>
                                    REF: {localData?.Header?.ReferenceNumber} | {localData?.Header?.Date}
                                </Typography>
                            </Box>
                        </Box>

                        <SectionHeader num="1" title="OWNER INFORMATION" />
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="OWNER NAME" value={localData?.OwnerInfo?.Name} />
                            <GridCell label="SALES REP" value={localData?.OwnerInfo?.SalesRep} />
                            <GridCell label="DATE" value={localData?.OwnerInfo?.Date} />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="JOB ADDRESS" value={localData?.OwnerInfo?.Address} flex={2} />
                            <GridCell label="CLAIM NUMBER" value={localData?.OwnerInfo?.ClaimNumber} />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="CITY" value={localData?.OwnerInfo?.City} />
                            <GridCell label="STATE" value={localData?.OwnerInfo?.State} />
                            <GridCell label="ZIP / COUNTY" value={localData?.OwnerInfo?.Zip} />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="PHONE" value={localData?.OwnerInfo?.Phone} />
                            <GridCell label="EMAIL" value={localData?.OwnerInfo?.Email} />
                            <GridCell label="INSURANCE COMPANY" value={localData?.OwnerInfo?.InsuranceCompany} />
                        </Stack>

                        <SectionHeader num="2" title="ROOFING - MATERIAL & UNDERLAYMENT" />
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="TEAR-OFF LAYERS" value={scope?.Layers} />
                            <GridCell label="ROOF SQUARES" value={scope?.Squares} />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%", alignItems: "stretch" }}>
                            <GridCell label="ROOF MATERIAL" value={scope?.Material} />
                            <GridCell label="ROOF COLOR" value={scope?.Color} />
                            <GridCell label="ROOF PITCH">
                                <PitchRowsContent
                                    rows={scope?.PitchRows}
                                    fallback={scope?.Pitch}
                                />
                            </GridCell>
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="UNDERLAYMENT" value={scope?.Underlayment} />
                            <GridCell label="ICE & WATER SHIELD" value={scope?.IceWaterShield} />
                            <GridCell label="STARTER STRIP" value={scope?.StarterStrip} />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="EAVE DRIP EDGE" value={scope?.EaveDripEdge} />
                            <GridCell label="RAKE DRIP EDGE" value={scope?.RakeDripEdge} />
                            <GridCell label="VALLEY TYPE" value={scope?.ValleyType} />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="HIP & RIDGE CAP" value={scope?.HipRidgeCap} flex={1} />
                        </Stack>

                        <SectionHeader num="3" title="NAILING, VENTILATION & FLASHING" />
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="FIELD NAIL SIZE" value={vents?.FieldNailSize} />
                            <GridCell label="FIELD NAIL QTY" value={vents?.FieldNailsPerShingle} />
                            <GridCell label="RIDGE NAIL SIZE" value={vents?.RidgeNailSize} />
                            <GridCell label="RIDGE NAIL QTY" value={vents?.RidgeNailsPerCap} />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%", alignItems: "stretch" }}>
                            <GridCell label="STEP FLASHING" value={vents?.StepFlashing} multiline />
                            <GridCell label="ROOF VENTILATION" value={vents?.RoofVentilation} multiline />
                            <GridCell label="PLUMBING VENTS" value={vents?.PlumbingVents} multiline />
                            <GridCell label="FURNACE VENT" value={vents?.FurnaceVent} multiline />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%", alignItems: "stretch" }}>
                            <GridCell label="RE-FLASH CHIMNEY" value={vents?.ReflashChimney} multiline />
                            <GridCell label="RE-FLASH SKYLIGHTS" value={vents?.ReflashSkylights} multiline />
                            <GridCell label="REPLACE SKYLIGHTS" value={vents?.ReplaceSkylights} multiline />
                        </Stack>

                        <SectionHeader num="4" title="SPECIAL PROJECT NOTES" />
                        <Box sx={{ p: 1.5, border: "0.2px solid", borderColor: "neutral.200", minHeight: 60 }}>
                            <Typography sx={{ fontSize: "11px", fontWeight: 500, color: "neutral.800", whiteSpace: "pre-line" }}>
                                {display(localData?.Notes)}
                            </Typography>
                        </Box>

                        <SectionHeader num="5" title="GUTTER SCOPE" />
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="REMOVE EXISTING GUTTERS" value={gutters?.RemoveExisting} />
                            <GridCell label="REMOVE EXISTING DOWNSPOUTS" value={gutters?.RemoveDownspouts} />
                            <GridCell label="INSTALL NEW GUTTERS" value={gutters?.InstallNew} />
                            <GridCell label="INSTALL NEW DOWNSPOUTS" value={gutters?.InstallDownspouts} />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="GUTTER LENGTH" value={gutters?.GutterLength} />
                            <GridCell label="DOWNSPOUT LENGTH" value={gutters?.DownspoutLength} />
                            <GridCell label="GUTTER SIZE" value={gutters?.GutterSize} />
                            <GridCell label="GUTTER COLOR" value={gutters?.GutterColor} />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="DOWNSPOUT SIZE" value={gutters?.DownspoutSize} />
                            <GridCell label="DOWNSPOUT COLOR" value={gutters?.DownspoutColor} />
                        </Stack>

                        <SectionHeader num="6" title="ADDITIONAL INFORMATION" />
                        <Box sx={{ p: 1.5, border: "0.2px solid", borderColor: "neutral.200", bgcolor: "#F7FFFA" }}>
                            <Stack spacing={0.5}>
                                {ADDITIONAL_INFO_ITEMS.map((item) => (
                                    <Typography key={item} sx={{ fontSize: "10px", color: "neutral.700" }}>
                                        ✓ {item}
                                    </Typography>
                                ))}
                            </Stack>
                        </Box>

                        <SectionHeader num="7" title="PRICING & PAYMENT" />
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="ROOF PRICE" value={pricing?.RoofPrice} />
                            <GridCell label="GUTTER PRICE" value={pricing?.GutterPrice} />
                            <GridCell label="TOTAL PRICE" value={pricing?.TotalPrice} />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="PAYMENT SCHEDULE" value={pricing?.PaymentSchedule} multiline flex={2} />
                            <GridCell label="ESTIMATED START DATE" value={pricing?.EstimatedStartDate} />
                        </Stack>
                        {pricing?.UpgradeAmount ? (
                            <Box
                                sx={{
                                    width: "100%",
                                    p: 1,
                                    border: "0.2px solid",
                                    borderColor: "neutral.200",
                                    bgcolor: "white",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: "8px",
                                        fontWeight: 700,
                                        color: "neutral.500",
                                        textTransform: "uppercase",
                                        mb: 0.5,
                                    }}
                                >
                                    Optional Shingle Upgrade
                                </Typography>
                                <Typography sx={{ ...cellTextSx, mb: 1, whiteSpace: "pre-line" }}>
                                    {`${pricing.UpgradeNotes || "Upgrade"} — ${pricing.UpgradeAmount}`}
                                </Typography>
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    sx={{
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        width: "100%",
                                    }}
                                >
                                    <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
                                        <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
                                            <Box
                                                sx={{
                                                    width: 12,
                                                    height: 12,
                                                    border: "1px solid",
                                                    borderColor: "neutral.500",
                                                    borderRadius: "2px",
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Typography sx={{ fontSize: "10px", fontWeight: 600, color: "neutral.800" }}>
                                                Accept
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
                                            <Box
                                                sx={{
                                                    width: 12,
                                                    height: 12,
                                                    border: "1px solid",
                                                    borderColor: "neutral.500",
                                                    borderRadius: "2px",
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Typography sx={{ fontSize: "10px", fontWeight: 600, color: "neutral.800" }}>
                                                Decline
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                    <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
                                        <Typography sx={{ fontSize: "10px", fontWeight: 600, color: "neutral.800" }}>
                                            Initials
                                        </Typography>
                                        <Box
                                            sx={{
                                                width: 64,
                                                borderBottom: "1px solid",
                                                borderColor: "neutral.500",
                                                height: 14,
                                            }}
                                        />
                                    </Stack>
                                </Stack>
                            </Box>
                        ) : null}

                        <SectionHeader num="8" title="SPECIAL CONDITIONS FOR INSURANCE CLAIMS" />
                        <Box sx={{ p: 1.5, bgcolor: COLOR_INSURANCE, border: "0.5px solid #E6D5A1" }}>
                            <Typography sx={{ fontSize: "9px", lineHeight: 1.4, color: "neutral.800" }}>
                                This agreement does not obligate the homeowners or 1st Colorado Roofing in any way
                                unless payment for damage is approved by both the homeowner’s insurance company as well
                                as 1st Colorado Roofing. By signing this agreement, the homeowner authorizes 1st
                                Colorado Roofing to pursue the homeowner&apos;s best interest for restoration of damage,
                                at the agreed upon price between the insurance company and 1st Colorado Roofing. There
                                will be no additional cost to the homeowner except for the deductible.
                            </Typography>
                        </Box>

                        <SectionHeader num="9" title="ACCEPTANCE & SIGNATURES" />
                        <Stack direction="row" sx={{ width: "100%", mt: 4, pt: 2, gap: 4 }}>
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ borderBottom: "1px solid", borderColor: "neutral.400", mt: 5, mb: 1 }} />
                                <Typography sx={{ fontSize: "8px", fontWeight: 700 }}>OWNER SIGNATURE</Typography>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ borderBottom: "1px solid", borderColor: "neutral.400", mt: 5, mb: 1 }} />
                                <Typography sx={{ fontSize: "8px", fontWeight: 700 }}>
                                    1ST COLORADO ROOFING REPRESENTATIVE
                                </Typography>
                            </Box>
                        </Stack>

                        <Box
                            sx={{
                                mt: 4,
                                textAlign: "center",
                                borderTop: "2px solid",
                                borderColor: COLOR_HEADER,
                                pt: 1.5,
                            }}
                        >
                            <Typography
                                sx={{ fontSize: "9px", fontWeight: 800, color: COLOR_HEADER, letterSpacing: "2px" }}
                            >
                                WWW.1STCOLORADOROOFING.COM
                            </Typography>
                        </Box>
                    </Sheet>
                </Box>

                <Box
                    sx={{
                        p: 3,
                        pt: 2,
                        display: "flex",
                        gap: 2,
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: "auto",
                        position: "sticky",
                        bottom: 0,
                        bgcolor: "background.level1",
                        borderTop: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    {onDownload ? (
                        <Button
                            variant="outlined"
                            color="neutral"
                            startDecorator={<FileDown size={20} />}
                            loading={isDownloading}
                            disabled={isDownloadDisabled}
                            onClick={onDownload}
                            sx={{ px: 3 }}
                        >
                            Download PDF
                        </Button>
                    ) : (
                        <Box />
                    )}

                    <Box sx={{ display: "flex", gap: 2, ml: "auto" }}>
                    {isSendDisabled ? (
                        <Button
                            variant="soft"
                            color="primary"
                            startDecorator={<RotateCw size={20} />}
                            loading={isGenerating}
                            onClick={() => onGenerate?.(localData)}
                            sx={{ px: 4 }}
                        >
                            Regenerate Contract
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant="outlined"
                                color="neutral"
                                startDecorator={<RotateCw size={20} />}
                                loading={isGenerating}
                                onClick={() => onGenerate?.(localData)}
                                sx={{ px: 4 }}
                            >
                                Regenerate Contract
                            </Button>
                            <Button
                                variant="soft"
                                color="primary"
                                startDecorator={<Send size={20} />}
                                loading={isSending}
                                onClick={onSend}
                                sx={{ px: 4 }}
                            >
                                Send for Signing
                            </Button>
                        </>
                    )}
                    </Box>
                </Box>
            </Box>
        </SideModal>
    );
}
